/**
 * MetaInfoManagerDialog — small power-user manager listing all loose
 * relationships and JSON-column declarations for the current
 * (connection, database). Allows removing individual rows and
 * importing/exporting the whole MetaInfo snapshot as JSON.
 *
 * Confirmation prompts for destructive actions are dispatched through the
 * app-level GlobalConfirmDialog (`openConfirmDialog`).
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  exportMetaInfo,
  importMetaInfo,
  removeEnumColumn,
  removeEnumGlyphDismissal,
  removeEnumValueCache,
  removeJsonColumn,
  removeLooseRelationship,
} from '../../services/metaInfoApi';
import type { EnumColumnDeclaration, EnumGlyphDismissal } from '@/types/electron';
import {
  useMetaInfo,
  useInvalidateMetaInfo,
} from '../../hooks/useMetaInfo';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import { toast } from '@/components/ui/toaster';
import { EnumColumnsSection } from './EnumColumnsSection';
import { JsonColumnsSection } from './JsonColumnsSection';
import { LooseRelationshipsList } from './LooseRelationshipsList';
import { openConfirmDialog } from '../confirm/confirmDialogStore';
import { openLooseRelationshipEditorDialog } from '../filters/looseRelationshipEditorDialogStore';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { isValidEngineId } from '../../../../../core/engines';

export type MetaInfoManagerDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  connectionId: string;
  database: string;
};

export function MetaInfoManagerDialog({
  open,
  onOpenChange,
  connectionId,
  database,
}: MetaInfoManagerDialogProps) {
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  const [importError, setImportError] = useState<string | null>(null);
  const [importWarnings, setImportWarnings] = useState<
    Array<{ relationshipId: string; side: 'source' | 'target'; missingColumn: string }>
  >([]);
  const engineRaw = useConnectionStore((s) =>
    s.connections.find((c) => c.id === connectionId)?.engine,
  );
  const engineForConnection = engineRaw && isValidEngineId(engineRaw) ? engineRaw : undefined;

  const onExport = async () => {
    const { filename, json } = await exportMetaInfo(connectionId, database);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    track('feature:metainfo_export');
  };

  const runImport = (mode: 'merge' | 'replace') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const json = await file.text();
        const result = await importMetaInfo(connectionId, database, json, mode);
        await invalidate(connectionId, database);
        track('feature:metainfo_import', { mode });
        setImportError(null);
        setImportWarnings((result as { warnings?: Array<{ relationshipId: string; side: 'source' | 'target'; missingColumn: string }> }).warnings ?? []);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Import failed';
        setImportError(msg);
        setImportWarnings([]);
      }
    };
    input.click();
  };

  const onImport = (mode: 'merge' | 'replace') => {
    if (mode === 'replace') {
      openConfirmDialog({
        title: 'Replace all meta-info?',
        description:
          'Replace all existing meta-info for this database with the imported file? Existing loose relationships and JSON-column declarations will be discarded.',
        confirmLabel: 'Replace',
        variant: 'destructive',
        onConfirm: () => runImport('replace'),
      });
      return;
    }
    runImport(mode);
  };

  const onRemoveLooseRel = (id: string, label: string) => {
    openConfirmDialog({
      title: 'Remove loose relationship?',
      description: `Remove the loose-FK declaration for ${label}? This won't delete data — only the relationship.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeLooseRelationship(connectionId, database, id);
        await invalidate(connectionId, database);
        track('feature:loose_fk_removed', { source: 'manager' });
      },
    });
  };

  const onRemoveJsonCol = (
    id: string,
    label: string,
    schema: string,
    table: string,
    column: string,
  ) => {
    openConfirmDialog({
      title: 'Remove JSON-column declaration?',
      description: `Remove the JSON-column declaration for ${label}? Filters and the tree-view cell renderer will fall back to plain-text behavior.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeJsonColumn(connectionId, database, id);
        await invalidate(connectionId, database);
        const removed = useWorkspaceStore
          .getState()
          .removeJsonOrphanFilters({ connectionId, database, schema, table, column });
        if (removed > 0) {
          toast.success({
            title: `Removed ${removed} JSON filter${removed === 1 ? '' : 's'} on ${column}`,
          });
        }
        track('feature:json_undeclare', { source: 'metainfo-manager' });
      },
    });
  };

  const onRemoveEnumCol = (d: EnumColumnDeclaration, label: string) => {
    const isPath = !!d.path;
    openConfirmDialog({
      title: isPath
        ? 'Remove JSON-path enum declaration?'
        : 'Remove enum-column declaration?',
      description: isPath
        ? `Remove the JSON-path enum declaration for ${label}? Filters on this path will fall back to a free-text input.`
        : `Remove the enum-column declaration for ${label}? Filters on this column will fall back to a free-text input.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeEnumColumn(connectionId, database, d.id);
        await invalidate(connectionId, database);
        if (d.path) {
          const removed = useWorkspaceStore
            .getState()
            .removeEnumPathOrphanFilters({
              connectionId,
              database,
              schema: d.schema,
              table: d.table,
              column: d.column,
              path: d.path,
            });
          if (removed > 0) {
            toast.success({
              title: `Removed ${removed} enum filter${removed === 1 ? '' : 's'} on ${d.path}`,
            });
          }
          await removeEnumValueCache(
            connectionId,
            database,
            d.schema,
            d.table,
            d.column,
            d.path,
          );
        }
        track('feature:enum_undeclare', {
          source: 'metainfo-manager',
          ...(d.path ? { on_json_path: true } : {}),
        });
      },
    });
  };

  const onRestoreEnumDismissal = async (d: EnumGlyphDismissal) => {
    await removeEnumGlyphDismissal(connectionId, database, {
      schema: d.schema,
      table: d.table,
      column: d.column,
    });
    await invalidate(connectionId, database);
    track('feature:enum_glyph_undismiss', {
      engine: engineForConnection ?? 'unknown',
    });
  };

  const relationships = metaInfo?.looseRelationships ?? [];
  const jsonColumns = metaInfo?.jsonColumns ?? [];
  const jsonStructureCache = metaInfo?.jsonStructureCache ?? [];
  const enumColumns = metaInfo?.enumColumns ?? [];
  const enumValueCache = metaInfo?.enumValueCache ?? [];
  const enumGlyphDismissals = metaInfo?.enumGlyphDismissals ?? [];
  const referenceTables = metaInfo?.referenceTables ?? [];
  const tableDisplayColumns = metaInfo?.tableDisplayColumns ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="metainfo-manager-dialog"
        // App-level GlobalConfirmDialog renders its AlertDialog as a sibling
        // of this dialog; clicks on the AlertDialog buttons would otherwise
        // dismiss this dialog. Allowlist `[role="alertdialog"]` so the
        // confirm flows (remove loose rel, remove JSON column, replace
        // import) don't auto-close MetaInfoManager. This is independent of
        // FilterWizard — see plan task 12 in
        // `Docs/superpowers/plans/2026-05-14-filter-wizard-portable-host.md`.
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest('[role="alertdialog"]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest('[role="alertdialog"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Database Meta-Info</DialogTitle>
          <DialogDescription>
            User-declared metadata that lives outside the schema. Loose relationships
            inform Jam SQL Studio's filter lookup, schema overview, and related-data
            views. JSON columns flag long-text columns so they get the JSON filter
            operator and tree-view cell renderer. Enum columns add a dedicated
            enum filter operator backed by a values dropdown sourced from native
            enum types, CHECK constraints, sampled data, or a referenced table —
            declared per column as a lookup enum, or once on the target table so
            every column pointing at it shows names.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <Button size="sm" onClick={onExport} data-testid="metainfo-export-btn">
            Export
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onImport('merge')}
            data-testid="metainfo-import-merge-btn"
          >
            Import (merge)
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onImport('replace')}
            data-testid="metainfo-import-replace-btn"
          >
            Import (replace)
          </Button>
        </div>
        {importError && (
          <div
            className="text-xs text-destructive border border-destructive/30 bg-destructive/5 p-2 rounded mb-2"
            data-testid="metainfo-import-error"
          >
            {importError}
          </div>
        )}
        {importWarnings.length > 0 && (
          <div
            className="text-xs text-amber-700 border border-amber-300/40 bg-amber-50/50 p-2 rounded mb-2"
            data-testid="metainfo-import-warnings"
          >
            <div className="font-medium mb-1">
              {importWarnings.length} filter{importWarnings.length === 1 ? '' : 's'} stripped on import
            </div>
            <ul className="list-disc pl-4">
              {importWarnings.map((w, i) => (
                <li key={i}>
                  Relationship <code>{w.relationshipId}</code>: {w.side} filter referenced missing column <code>{w.missingColumn}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-xs font-semibold mt-1 mb-1">Loose relationships</div>
        <div
          className="flex flex-col gap-2 max-h-[200px] overflow-auto mb-3"
          data-testid="metainfo-list"
        >
          <LooseRelationshipsList
            relationships={relationships}
            onRemove={(r, label) => onRemoveLooseRel(r.id, label)}
            onShowDetails={(r) => {
              track('feature:metainfo_manager_loose_fk_details');
              openLooseRelationshipEditorDialog({
                connectionId,
                database,
                engine: engineForConnection,
                source: {
                  schema: r.fromSchema,
                  table: r.fromTable,
                  name: r.fromColumn,
                  dataType: '',
                },
                existingRelationshipId: r.id,
                source_label: 'metainfo_manager_details',
              });
            }}
          />
        </div>

        <div className="text-xs font-semibold mt-1 mb-1">JSON columns</div>
        <div
          className="flex flex-col gap-1 max-h-[200px] overflow-auto mb-3"
          data-testid="metainfo-json-list"
        >
          <JsonColumnsSection
            declarations={jsonColumns}
            structureCache={jsonStructureCache}
            onRemove={(d) =>
              onRemoveJsonCol(
                d.id,
                `${d.schema}.${d.table}.${d.column}`,
                d.schema,
                d.table,
                d.column,
              )
            }
            connectionId={connectionId}
            database={database}
            engine={engineForConnection}
          />
        </div>

        <div className="text-xs font-semibold mt-1 mb-1">Enum columns</div>
        <div
          className="flex flex-col gap-1 max-h-[200px] overflow-auto"
          data-testid="metainfo-enum-list"
        >
          <EnumColumnsSection
            declarations={enumColumns}
            valueCache={enumValueCache}
            onRemove={(d) =>
              onRemoveEnumCol(
                d,
                `${d.schema}.${d.table}.${d.column}${d.path ? ` · ${d.path}` : ''}`,
              )
            }
            dismissals={enumGlyphDismissals}
            onRestoreDismissal={onRestoreEnumDismissal}
            connectionId={connectionId}
            database={database}
            engine={engineForConnection}
            referenceTables={referenceTables}
            tableDisplayColumns={tableDisplayColumns}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
